"use client";

import { useState, useRef, useEffect, memo, useMemo } from "react";
import { useRouter } from "next/navigation";
import type { AiProfile } from "@/types/ai";
import { FolderKanban, Settings, Mic, Paperclip, Sparkles, X, MessageSquare, Compass, Square } from "lucide-react";
import { motion } from "framer-motion";
import AtlasFullscreenView from "./UltraNavAtlasFullscreen";
import PremiumSendButton from "./PremiumSendButton";
import ResponseModeSelector from "./ResponseModeSelector";
import TemplateMultiButton from "./TemplateMultiButton";
import type { ResponseMode } from "@/lib/ai/responseModes";
import { detectBestMode } from "@/lib/ai/responseModes";
import { getInputPlaceholder } from "@/lib/utils/microcopy";
import { loadKarmaProfile } from "@/lib/karma/profileManager";
import { getTextColorForBackground, getTextColorWithOpacity } from "@/lib/utils/colorContrast";
import type { AtlasSearchProcess, AtlasNavigationResult } from "@/lib/atlas/integration";

/** Short Atlas status text (max ~24 chars) for the minimized bar. */
function getAtlasStatusText(
  atlasProcess: AtlasSearchProcess | null,
  atlasNavigationResult: AtlasNavigationResult | null
): string {
  if (!atlasProcess) return "";
  switch (atlasProcess.status) {
    case "planning":
    case "navigating":
      return "Navigating";
    case "orienting":
      return "Orienting";
    case "ready":
    case "complete":
      if (atlasNavigationResult?.location?.topicName) {
        const location = atlasNavigationResult.location.topicName;
        return location.length > 24 ? `${location.substring(0, 21)}...` : location;
      }
      if (atlasNavigationResult?.orientation?.currentTopic) {
        const topic = atlasNavigationResult.orientation.currentTopic;
        return topic.length > 24 ? `${topic.substring(0, 21)}...` : topic;
      }
      return "Ready";
    default:
      return "Idle";
  }
}

/** Contextual input placeholder from profile, builder mode, and KARMA. */
function getContextualPlaceholder(opts: {
  isBuilderMode: boolean;
  profile: AiProfile;
  karmaProfile: any;
  configPlaceholder?: string;
}): string {
  const { isBuilderMode, profile, karmaProfile, configPlaceholder } = opts;
  if (isBuilderMode) return "Describe how you want your AI to look and behave...";
  if (profile.modePreset) {
    const modePreset = profile.modePreset.toLowerCase();
    if (modePreset.includes("support") || modePreset.includes("customer")) return "Ask a product question?";
    if (modePreset.includes("code") || modePreset.includes("developer")) return "Refactor the selected code?";
    if (modePreset.includes("research") || modePreset.includes("analyst")) return "What would you like to research?";
  }
  if (karmaProfile) return getInputPlaceholder(profile, karmaProfile);
  return configPlaceholder || "Build your dream...";
}

const BUILDER_MODES = [
  { id: "preview", label: "Preview" },
  { id: "customize", label: "Customize" },
  { id: "code", label: "Code" },
  { id: "instructions", label: "Instructions" },
  { id: "analytics", label: "Analytics" },
  { id: "integrations", label: "Integrations" },
  { id: "knowledge", label: "Knowledge" },
  { id: "advanced", label: "Advanced" },
] as const;

export interface ImageAttachment {
  type: "image";
  base64: string;
  mimeType?: string;
}

interface Props {
  profile: AiProfile;
  input: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onStop?: () => void;
  loading: boolean;
  responseMode: ResponseMode;
  onResponseModeChange: (mode: ResponseMode) => void;
  karmaProfile?: any;
  atlasProcess?: AtlasSearchProcess | null;
  atlasNavigationResult?: AtlasNavigationResult | null;
  atlasHistory?: Array<{
    process: AtlasSearchProcess;
    result: AtlasNavigationResult;
    messageId: string;
    timestamp: number;
  }>;
  isVoiceActive?: boolean;
  isBuilderMode?: boolean; // When true, shows builder-specific layout
  builderActiveMode?: string; // Current active mode in builder (chat, preview, etc.)
  onBuilderModeChange?: (mode: string) => void; // Callback to change builder mode
  /** Image attachments for vision input (upload image + question) */
  attachments?: ImageAttachment[];
  onAttachmentsChange?: (attachments: ImageAttachment[]) => void;
  /** When provided, voice button calls this instead of no-op (e.g. open voice conversation). */
  onVoiceClick?: () => void;
}

/**
 * Ultra Navigation Bar
 * 
 * A single, cohesive, adaptive navigation bar that replaces fragmented navigation.
 * Features:
 * - Resting State: Compact AI toolbar with Builder, CTA, Settings, Voice
 * - Active State: Full-width adaptive input field with contextual features
 * - Ultra Aura: Pulsating glow effect using user's custom theme colors
 */
function UltraNavigationBar({
  profile,
  input,
  onInputChange,
  onSend,
  onStop,
  loading,
  responseMode,
  onResponseModeChange,
  karmaProfile,
  atlasProcess = null,
  atlasNavigationResult = null,
  atlasHistory = [],
  isVoiceActive = false,
  isBuilderMode = false,
  builderActiveMode,
  onBuilderModeChange,
  attachments = [],
  onAttachmentsChange,
  onVoiceClick,
}: Props) {
  const router = useRouter();
  const [isActive, setIsActive] = useState(!!input.trim());
  const [karmaProfileState, setKarmaProfileState] = useState(karmaProfile);
  const inputRef = useRef<HTMLInputElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const atlasContainerRef = useRef<HTMLDivElement>(null);
  
  // Atlas expansion state: 0 = minimized (thin line), 2 = fullscreen (slide up)
  const [atlasExpansionState, setAtlasExpansionState] = useState<0 | 2>(0);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const startYRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);

  // Atlas stays minimized unless user swipes up - no auto-pop

  // Handle swipe gestures for Atlas expansion
  useEffect(() => {
    if (!atlasProcess || !atlasContainerRef.current) return;
    
    const container = atlasContainerRef.current;
    const swipeThreshold = 80;

    const handleStart = (clientY: number) => {
      startYRef.current = clientY;
      isDraggingRef.current = true;
    };

    const handleMove = (clientY: number) => {
      if (!isDraggingRef.current || startYRef.current === null) return;
      
      const deltaY = startYRef.current - clientY; // Positive = swiping up

      if (deltaY > 10 && atlasExpansionState === 0) {
        // Swiping up - expand to fullscreen
        const progress = Math.min(deltaY / swipeThreshold, 1);
        setSwipeProgress(progress);
        if (progress > 0.5) {
          setAtlasExpansionState(2);
          setSwipeProgress(1);
        }
      } else if (deltaY < -10 && atlasExpansionState === 2) {
        // Swiping down - collapse to minimized
        const swipeDown = Math.abs(deltaY);
        const progress = Math.max(1 - (swipeDown / swipeThreshold), 0);
        setSwipeProgress(progress);
        if (progress < 0.3) {
          setAtlasExpansionState(0);
          setSwipeProgress(0);
        }
      }
    };

    const handleEnd = () => {
      if (isDraggingRef.current) {
        if (atlasExpansionState === 0 && swipeProgress > 0.3) {
          setAtlasExpansionState(2);
        } else if (atlasExpansionState === 2 && swipeProgress < 0.7) {
          setAtlasExpansionState(0);
        }
        setSwipeProgress(0);
        isDraggingRef.current = false;
        startYRef.current = null;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      handleStart(e.touches[0].clientY);
    };
    const handleTouchMove = (e: TouchEvent) => {
      handleMove(e.touches[0].clientY);
    };
    const handleTouchEnd = () => {
      handleEnd();
    };

    const handleMouseDown = (e: MouseEvent) => {
      handleStart(e.clientY);
      const handleMouseMove = (e: MouseEvent) => handleMove(e.clientY);
      const handleMouseUp = () => {
        handleEnd();
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchmove', handleTouchMove);
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('mousedown', handleMouseDown);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('mousedown', handleMouseDown);
    };
  }, [atlasProcess, atlasExpansionState, swipeProgress]);

  // Auto-expand if input has content (skip in builder mode - always active)
  useEffect(() => {
    // Only update if we need to set it to true (avoid unnecessary updates)
    if (isBuilderMode) {
      setIsActive((prev) => prev ? prev : true);
    } else if (input.trim()) {
      setIsActive((prev) => prev ? prev : true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, isBuilderMode]); // Removed isActive from deps to prevent infinite loop

  // Load KARMA profile if not provided
  useEffect(() => {
    if (!karmaProfileState) {
      loadKarmaProfile(profile.id)
        .then(setKarmaProfileState)
        .catch(console.error);
    }
  }, [profile.id, karmaProfileState]);

  // Get user's custom theme colors for the Aura
  const primaryColor = profile.brand?.primaryColor || "#87CEEB";
  const secondaryColor = profile.brand?.secondaryColor || "#B0E0E6";
  // Ensure bar labels and CTA are always readable on any theme (ultra bar + Atlas)
  const barTextColor = getTextColorForBackground(primaryColor);

  // Get Ultra Nav config with defaults
  const navConfig = profile.brand?.ultraNavConfig || {};
  const config = {
    enabled: navConfig.enabled !== false, // Default: enabled
    showBuilder: navConfig.showBuilder !== false,
    showMarketplace: navConfig.showMarketplace !== false,
    showSettings: navConfig.showSettings !== false,
    showVoice: navConfig.showVoice !== false,
    showResponseMode: navConfig.showResponseMode !== false,
    showAttachment: navConfig.showAttachment !== false,
    showTemplates: navConfig.showTemplates !== false,
    auraEnabled: navConfig.auraEnabled !== false,
    auraIntensity: navConfig.auraIntensity ?? 0.5,
    auraSpeed: navConfig.auraSpeed || "normal",
    restingStateCTA: navConfig.restingStateCTA || profile.name || "Build Your Dream",
    placeholder: navConfig.placeholder,
    position: navConfig.position || "bottom",
    size: navConfig.size || "medium",
  };
  
  // Don't render if disabled
  if (!config.enabled) {
    return null;
  }

  // Handle focus - expand to active state
  const handleFocus = () => {
    setIsActive(true);
  };

  // Handle blur - collapse if input is empty
  const handleBlur = () => {
    if (!input.trim()) {
      setIsActive(false);
    }
  };

  // Handle CTA click - expand and focus
  const handleCTAClick = () => {
    setIsActive(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Handle send - collapse if input is cleared
  const handleSendClick = () => {
    onSend();
    // Don't collapse immediately - let the parent handle input clearing
    setTimeout(() => {
      if (!input.trim() && !(attachments?.length)) {
        setIsActive(false);
      }
    }, 100);
  };

  // Attach image: open file picker and add as base64
  const handleAttachClick = () => fileInputRef.current?.click();
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !onAttachmentsChange) return;
    const readFile = (file: File): Promise<ImageAttachment | null> =>
      new Promise((resolve) => {
        if (!file.type.startsWith("image/")) {
          resolve(null);
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1]! : dataUrl;
          const mimeType = dataUrl.match(/data:([^;]+)/)?.[1] || file.type;
          resolve({ type: "image", base64, mimeType });
        };
        reader.readAsDataURL(file);
      });
    const newAttachments = await Promise.all(Array.from(files).map(readFile));
    const added = newAttachments.filter((a): a is ImageAttachment => a != null);
    if (added.length) onAttachmentsChange([...(attachments || []), ...added]);
    e.target.value = "";
  };
  const removeAttachment = (index: number) => {
    if (!onAttachmentsChange) return;
    const next = (attachments || []).filter((_, i) => i !== index);
    onAttachmentsChange(next);
  };

  // Calculate text colors for input
  const inputBackgroundColor = "#ffffff";
  const inputTextColor = getTextColorForBackground(inputBackgroundColor);
  const inputPlaceholderColor = getTextColorWithOpacity(inputBackgroundColor, 0.5);
  const inputIconColor = getTextColorWithOpacity(inputBackgroundColor, 0.7);

  // Generate Aura glow colors from user's theme
  const auraColors = [primaryColor, secondaryColor, primaryColor, secondaryColor];

  // Calculate aura animation duration based on speed
  const auraDuration = config.auraSpeed === "slow" ? "4s" : config.auraSpeed === "fast" ? "2s" : "3s";

  // Stable style objects to avoid new references every render (helps memoized children)
  const auraStyle = useMemo(
    () =>
      config.auraEnabled
        ? {
            background: `radial-gradient(ellipse at center, ${primaryColor}${Math.round(40 * config.auraIntensity)} 0%, ${secondaryColor}${Math.round(30 * config.auraIntensity)} 50%, transparent 70%)`,
            animation: `auraPulse ${auraDuration} ease-in-out infinite`,
            filter: "blur(40px)",
            WebkitFilter: "blur(40px)",
            transform: config.position === "top" ? "translateY(-20%)" : "translateY(20%)",
          }
        : undefined,
    [primaryColor, secondaryColor, config.auraEnabled, config.auraIntensity, config.position, auraDuration]
  );
  const mainBarContainerStyle = useMemo(
    () => ({
      maxWidth: isActive && !isBuilderMode ? "48rem" : isBuilderMode ? "100%" : "none",
      touchAction: "pan-y" as const,
      width: isBuilderMode ? "100%" : undefined,
      overflow: "hidden",
      boxSizing: "border-box" as const,
    }),
    [isActive, isBuilderMode]
  );

  return (
    <div className={`${isBuilderMode ? "relative" : "fixed"} ${config.position === "top" ? "top-0" : "bottom-0"} left-0 right-0 z-50 ${config.position === "bottom" && !isBuilderMode ? "pb-safe" : ""} ${config.position === "top" && !isBuilderMode ? "pt-safe" : ""}`}>
      {/* Builder Mode: Mode Switcher Tabs */}
      {isBuilderMode && onBuilderModeChange && (
        <div className="px-4 py-2 flex items-center justify-center gap-2 border-b border-purple-500/20 overflow-x-auto">
          {BUILDER_MODES.map((mode) => (
            <button
              key={mode.id}
              onClick={() => onBuilderModeChange(mode.id)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                builderActiveMode === mode.id
                  ? "bg-white/20 text-white shadow-lg"
                  : "bg-white/10 text-white/70 hover:bg-white/15"
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>
      )}

      {/* Ultra Aura - Pulsating glow effect (if enabled) */}
      {config.auraEnabled && auraStyle && (
        <div className="absolute inset-0 pointer-events-none" style={auraStyle} aria-hidden="true" />
      )}
      
      {/* Main Bar Container - Swipeable for Atlas Thought Stream */}
      <div
        ref={barRef}
        data-ultra-nav-bar
        className={`relative ${isBuilderMode ? "mx-0 w-full" : "mx-auto"} ${isBuilderMode ? "" : "mb-4"} transition-all duration-500 ease-out ${
          isActive && !isBuilderMode
            ? "w-[95%] max-w-3xl" 
            : isBuilderMode ? "w-full" : "w-auto"
        }`}
        style={mainBarContainerStyle}
      >
        {/* Resting State - Compact AI Toolbar with Liquid Glass (skip in builder mode) */}
        {!isActive && !isBuilderMode && (
          <div
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full transition-all duration-300 focus-within:outline-none"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}12 0%, ${secondaryColor}12 100%)`,
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              border: `1px solid ${primaryColor}25`,
              boxShadow: `0 2px 16px ${primaryColor}20, 0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.1)`,
              outline: "none",
            }}
            onFocus={(e) => e.currentTarget.style.outline = "none"}
            tabIndex={-1}
          >
            {/* Action 1: Builder Icon - if enabled */}
            {config.showBuilder && (
              <button
                onClick={() => router.push("/builder")}
                className="p-2 rounded-full hover:bg-white/15 transition-all duration-200 hover:scale-105 active:scale-95 flex-shrink-0"
                title="Builder"
                aria-label="Open Builder"
              >
                <FolderKanban className="w-4 h-4" style={{ color: barTextColor }} />
              </button>
            )}

            {/* Multi-Chat Icon */}
            <button
              onClick={() => {
                localStorage.setItem("multi_chat_enabled", "true");
                router.push("/chat?mode=multi");
              }}
              className="p-2 rounded-full hover:bg-white/15 transition-all duration-200 hover:scale-105 active:scale-95 flex-shrink-0"
              title="Multi-Chat Mode"
              aria-label="Open Multi-Chat"
            >
              <MessageSquare className="w-4 h-4" style={{ color: barTextColor }} />
            </button>

            {/* Primary CTA (Center) - high contrast so readable on any theme */}
            <button
              onClick={handleCTAClick}
              className="flex-1 px-4 py-2 text-center text-sm font-medium transition-all duration-200 rounded-full hover:bg-white/10 active:scale-98"
              style={{ color: barTextColor }}
            >
              {config.restingStateCTA}
            </button>

            {/* Voice Icon - if enabled */}
            {config.showVoice && (
              <button
                onClick={() => onVoiceClick?.()}
                className="p-2 rounded-full hover:bg-white/15 transition-all duration-200 hover:scale-105 active:scale-95 flex-shrink-0 relative"
                title="Voice input"
                aria-label="Start voice input"
              >
                <Mic className="w-4 h-4" style={{ color: barTextColor }} />
                {isVoiceActive && (
                  <div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      background: `radial-gradient(circle, ${primaryColor}40 0%, ${secondaryColor}30 50%, transparent 70%)`,
                      animation: "soundwavePulse 1.5s ease-in-out infinite",
                      filter: "blur(8px)",
                      WebkitFilter: "blur(8px)",
                    }}
                    aria-hidden="true"
                  />
                )}
              </button>
            )}

            {/* Widget/Marketplace Icon */}
            {config.showMarketplace && (
              <button
                onClick={() => router.push("/marketplace?tab=widgets")}
                className="p-2 rounded-full hover:bg-white/15 transition-all duration-200 hover:scale-105 active:scale-95 flex-shrink-0"
                title="Widgets & APIs"
                aria-label="Open Widget Marketplace"
              >
                <Sparkles className="w-4 h-4" style={{ color: barTextColor }} />
              </button>
            )}

            {/* Settings Icon */}
            {config.showSettings && (
              <button
                onClick={() => router.push("/settings")}
                className="p-2 rounded-full hover:bg-white/15 transition-all duration-200 hover:scale-105 active:scale-95 flex-shrink-0"
                title="Settings"
                aria-label="Open Settings"
              >
                <Settings className="w-4 h-4" style={{ color: barTextColor }} />
              </button>
            )}
          </div>
        )}

        {/* Active State - Ultra Adaptive Input Field with Liquid Glass - SINGLE UNIFIED CONTAINER - ALWAYS ROUNDED PILL */}
        {/* In builder mode, always show input */}
        {(isActive || isBuilderMode) && (
          <div
            className={`flex flex-col gap-0 ${isBuilderMode ? "px-4 py-2" : "px-3 sm:px-4"} transition-all duration-300 focus-within:outline-none`}
            style={{
              background: isBuilderMode 
                ? `linear-gradient(135deg, ${primaryColor}15 0%, ${secondaryColor}15 100%)`
                : `linear-gradient(135deg, ${primaryColor}10 0%, ${secondaryColor}10 100%)`,
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              border: isBuilderMode ? `1px solid ${primaryColor}30` : `1px solid ${primaryColor}25`,
              borderRadius: isBuilderMode ? "12px" : "9999px",
              boxShadow: `0 2px 16px ${primaryColor}20, 0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.1)`,
              outline: "none",
              paddingTop: isBuilderMode ? "0.5rem" : (atlasProcess && atlasExpansionState === 0 ? "0.375rem" : "0.5rem"),
              paddingBottom: "0.5rem",
            }}
            onFocus={(e) => e.currentTarget.style.outline = "none"}
            tabIndex={-1}
          >
            {/* Attachment previews */}
            {attachments && attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {attachments.map((att, i) => (
                  <div
                    key={i}
                    className="relative inline-flex rounded-lg overflow-hidden border shrink-0"
                    style={{ borderColor: `${primaryColor}40` }}
                  >
                    <img
                      src={`data:${att.mimeType || "image/png"};base64,${att.base64}`}
                      alt=""
                      className="w-10 h-10 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="absolute top-0 right-0 p-0.5 bg-black/60 rounded-bl text-white hover:bg-black/80"
                      aria-label="Remove attachment"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Atlas Minimized State - Thin line above input */}
            {atlasProcess && atlasExpansionState === 0 && (
              <motion.div
                ref={atlasContainerRef}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "28px", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="mb-1.5 cursor-pointer"
                style={{ touchAction: "pan-y" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setAtlasExpansionState(2);
                }}
              >
                <div
                  className="w-full h-full rounded-full flex items-center gap-2 px-3 transition-all hover:scale-[1.01] active:scale-[0.99]"
                  style={{
                    background: `linear-gradient(90deg, ${primaryColor}12 0%, ${secondaryColor}15 50%, ${primaryColor}12 100%)`,
                    backdropFilter: "blur(12px) saturate(180%)",
                    WebkitBackdropFilter: "blur(12px) saturate(180%)",
                    border: `1px solid ${primaryColor}25`,
                    boxShadow: `0 2px 12px ${primaryColor}20, inset 0 1px 0 rgba(255,255,255,0.15)`,
                  }}
                >
                  <Compass className="w-3 h-3 flex-shrink-0" style={{ color: barTextColor }} />
                  <span className="text-xs font-medium truncate flex-1" style={{ color: barTextColor }}>
                    {getAtlasStatusText(atlasProcess, atlasNavigationResult)}
                  </span>
                </div>
              </motion.div>
            )}

            {/* Input Row - Always visible */}
            <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Response Mode Selector - Compact (if enabled) */}
            {config.showResponseMode && (
              <div className="flex-shrink-0">
                <ResponseModeSelector
                  profile={profile}
                  currentMode={responseMode}
                  onModeChange={(mode) => {
                    onResponseModeChange(mode);
                    if (mode === "smart" && input.trim()) {
                      const detected = detectBestMode(input);
                      if (detected !== "smart") {
                        onResponseModeChange(detected);
                      }
                    }
                  }}
                />
              </div>
            )}

            {/* Attachment Icon - if enabled */}
            {config.showAttachment && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  multiple
                  onChange={handleFileChange}
                  aria-label="Attach image"
                />
                <button
                  type="button"
                  className="p-1.5 rounded-full hover:bg-white/15 transition-all duration-200 hover:scale-105 active:scale-95 flex-shrink-0"
                  title="Attach image"
                  aria-label="Attach image"
                  style={{ color: inputIconColor }}
                  onClick={handleAttachClick}
                >
                  <Paperclip className="w-4 h-4" />
                </button>
              </>
            )}

            {/* Template Multi-Button (Templates + Ghost Pills) */}
            {config.showTemplates && (
              <TemplateMultiButton
                profile={profile}
                onSuggestionClick={(suggestion) => {
                  onInputChange(suggestion);
                  setIsActive(true);
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 100);
                }}
                inputIconColor={inputIconColor}
              />
            )}

            {/* Input Field */}
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => onInputChange(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={getContextualPlaceholder({ isBuilderMode, profile, karmaProfile: karmaProfileState, configPlaceholder: config.placeholder })}
              className="flex-1 bg-transparent focus:outline-none text-sm sm:text-base min-w-0 placeholder:opacity-60"
              disabled={loading}
              aria-label="Message input"
              style={{
                minHeight: "32px",
                color: inputTextColor,
              }}
            />

            {/* Stop / Send Button */}
            {loading && onStop ? (
              <button
                type="button"
                onClick={onStop}
                aria-label="Stop generating"
                className="p-2 md:p-2.5 rounded-lg md:rounded-xl flex-shrink-0 flex items-center justify-center text-white transition-all duration-200 hover:opacity-90 active:scale-95"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                  minWidth: "44px",
                  minHeight: "44px",
                  boxShadow: `0 4px 12px ${primaryColor}40, inset 0 1px 0 rgba(255,255,255,0.2)`,
                }}
              >
                <Square className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2.5} />
              </button>
            ) : (
              <PremiumSendButton
                profile={profile}
                onClick={handleSendClick}
                disabled={(!input.trim() && !(attachments?.length)) || loading}
                loading={loading}
                config={{
                  shape: "rounded",
                  size: "medium",
                  icon: "arrow",
                  hoverEffect: "scale",
                }}
              />
            )}

            </div>

          </div>
        )}
      </div>

      {/* Atlas Fullscreen View - State 2: Slide up to fullscreen */}
      {atlasProcess && atlasExpansionState === 2 && (
        <AtlasFullscreenView
          profile={profile}
          atlasProcess={atlasProcess}
          atlasNavigationResult={atlasNavigationResult}
          atlasHistory={atlasHistory}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          onClose={() => setAtlasExpansionState(0)}
        />
      )}
    </div>
  );
}

export default memo(UltraNavigationBar);